import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin, googleLogout } from '@react-oauth/google';
import Profile from './pages/Profile';
import Navbar from './Navbar';
import Home from './pages/ThoughtBubbles';
import About from './pages/About';
import Contact from './pages/Contact';
import Tool from './pages/Tool';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';
import PricingSuccess from './pages/PricingSuccess';
import Settings from './pages/TermsModal';
import OutplacementServices from './pages/OutplacementServices';
import Referral from './components/ui/Referral';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import './App.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import TermsModal from './pages/TermsModal';
import FAQ from './pages/FAQ';
import SignUp from './pages/SignUp';
import OnboardingModal from './components/ui/OnboardingModal';
import Extension from './pages/Extension';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { syncResumeProfileOnLogin } from './utils/resumeProfileAPI';
import LinkedInCallback from './pages/LinkedInCallback';
import { authFetch, setAuthToken, clearAuthToken } from './utils/authFetch';

// ProtectedRoute component
function ProtectedRoute({ children }) {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  if (!isLoggedIn) {
    return <Navigate to="/signup" replace />;
  }
  return children;
}

function App() {
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [output, setOutput] = useState('');
  const [storedResults, setStoredResults] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userEmail, setUserEmail] = useState(null);
  const location = useLocation();
  const [userPlan, setUserPlan] = useState(null);
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem('selectedTab') || 'Tool';
  });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!userEmail) return;

    (async () => {
      try {
        const url = `${apiUrl}/get_user_info?user_id=${encodeURIComponent(userEmail)}`;
        const res = await authFetch(url);
        const rawText = await res.text();

        let body = rawText;
        if (body.startsWith('"') && body.endsWith('"')) {
          body = body.slice(1, -1);
        }
        body = body
          .replace(/^User information:\s*/, '')
          .replace(/'/g, '"')
          .replace(/Decimal\("(\d+)"\)/g, '$1');

        const parsed = JSON.parse(body);

        if (!parsed.Item || !parsed.Item.PlanType) {
          await authFetch(`${apiUrl}/set_max_searches`, {
            method: 'POST',
            body: JSON.stringify({
              user_id: userEmail,
              MaxSearches: 3,
              plan_type: 'Free Tier / 3-URL Daily Search Plan',
              Active: 'No'
            })
          });
          setUserPlan('Free Tier / 3-URL Daily Search Plan');
          return;
        }

        const { PlanType, MaxSearches: currentMaxRaw } = parsed.Item;
        setUserPlan(PlanType);
        const currentMax = Number(currentMaxRaw);
        const normalizedPlan = PlanType.toLowerCase().trim();

        const planInfoMap = {
          'free/weekly plan': { max: 3, Active: 'No' },
          'free tier / 3-url daily search plan': { max: 3, Active: 'No' },
          'casual job seeker': { max: 10, Active: 'Yes' },
          'active job seeker/ hourly search plan': { max: 25, Active: 'Yes' },
          'owner': { max: 1000, Active: 'Yes' }
        };
        const planInfo = planInfoMap[normalizedPlan];
        if (!planInfo) return;

        if (currentMax !== planInfo.max) {
          await authFetch(`${apiUrl}/set_max_searches`, {
            method: 'POST',
            body: JSON.stringify({
              user_id: userEmail,
              MaxSearches: planInfo.max,
              plan_type: PlanType,
              Active: planInfo.Active
            })
          });
        }
      } catch (err) {
        // Silent error
      }
    })();
  }, [userEmail]);

  const fetchStoredUrls = async (userId) => {
    try {
      const response = await authFetch(`${apiUrl}/get_stored_urls?user_id=${userId}`);

      if (response.status === 404) {
        setStoredResults([]);
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch stored URLs.");
      }
      const data = await response.json();

      const groupedData = data.reduce((acc, entry) => {
        const {
          search_id, url, base_url, keyword, match_type,
          runtime, epoch_time, company_name, match_status_changed,
          frequency, mark_as_applied,
        } = entry;

        const groupKey = base_url || url;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            search_id: groupKey,
            url: base_url || url,
            base_url: base_url || url,
            company_name: company_name || extractCompanyName(url),
            runtime: runtime || "0s",
            timeRaw: epoch_time || Date.now() / 1000,
            time: epoch_time || Date.now() / 1000,
            timeFormatted: new Date((epoch_time || Date.now() / 1000) * 1000).toLocaleString(),
            keywords: [],
            frequency: frequency || "None",
            match_status_found: match_type === "exact" || match_type === "partial",
          };
        } else {
          if (match_type === "exact" || match_type === "partial") {
            acc[groupKey].match_status_found = true;
          }
        }

        acc[groupKey].keywords.push({
          keyword: keyword || "Unknown",
          match_type: match_type || "not-found",
          url: url,
          search_id: search_id,
          mark_as_applied: mark_as_applied || false,
          status: "complete"
        });

        return acc;
      }, {});

      setStoredResults(Object.values(groupedData));
    } catch (error) {
      // Silent error
    }
  };

  // ✅ Startup session restore — validates token before restoring session
  useEffect(() => {
    const storedLoginState = localStorage.getItem('isLoggedIn');
    const storedUserEmail = localStorage.getItem('userEmail');
    const storedFirstName = localStorage.getItem('userFirstName');
    const storedLastName = localStorage.getItem('userLastName');

    if (storedLoginState === 'true' && storedUserEmail) {
      const token = localStorage.getItem('authToken');
      const setAt = localStorage.getItem('authTokenSetAt');
      const ageMs = setAt ? Date.now() - parseInt(setAt, 10) : Infinity;
      const tokenValid = token && ageMs < 55 * 60 * 1000; // 55 minutes

      if (!tokenValid) {
        // Token expired while away — clear everything and let ProtectedRoute redirect
        clearAuthToken();
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userFirstName');
        localStorage.removeItem('userLastName');
        localStorage.removeItem('loginSource');
        return;
      }

      setIsLoggedIn(true);
      setUserEmail(storedUserEmail);
      if (storedFirstName) setUserFirstName(storedFirstName);
      if (storedLastName) setUserLastName(storedLastName);
      fetchStoredUrls(storedUserEmail);
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/tool') {
      const storedTab = localStorage.getItem('selectedTab') || 'Tool';
      setSelectedTab(storedTab);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (localStorage.getItem('isLoggedIn') === 'true') {
      const email = localStorage.getItem('userEmail');
      const firstName = localStorage.getItem('userFirstName');
      const lastName = localStorage.getItem('userLastName');
      if (email && email !== userEmail) setUserEmail(email);
      if (firstName && firstName !== userFirstName) setUserFirstName(firstName);
      if (lastName && lastName !== userLastName) setUserLastName(lastName);
    }
  }, [location.pathname, isLoggedIn]);

  useEffect(() => {
    const savedResults = localStorage.getItem("storedResults");
    if (savedResults) setStoredResults(JSON.parse(savedResults));
  }, []);

  useEffect(() => {
    localStorage.setItem("storedResults", JSON.stringify(storedResults));
  }, [storedResults]);

  useEffect(() => {
    localStorage.setItem('selectedTab', selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    AOS.init({ duration: 800, once: false, offset: 100 });
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    const html = document.documentElement;
    if (showTermsModal) {
      document.body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
      if (root) { root.style.position = 'fixed'; root.style.width = '100%'; }
    } else {
      document.body.style.overflow = '';
      html.style.overflow = '';
      if (root) { root.style.position = ''; root.style.width = ''; }
    }
    return () => {
      document.body.style.overflow = '';
      html.style.overflow = '';
      if (root) { root.style.position = ''; root.style.width = ''; }
    };
  }, [showTermsModal]);

  useEffect(() => {
    if (showTermsModal) {
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    }
  }, [showTermsModal]);

  useEffect(() => {
    if (isLoggedIn && userEmail) {
      const accepted = localStorage.getItem(`termsAccepted_${userEmail}`);
      if (!accepted) setShowTermsModal(true);
    }
  }, [isLoggedIn, userEmail]);

  useEffect(() => {
    if (isLoggedIn && userEmail) {
      const termsAccepted = localStorage.getItem(`termsAccepted_${userEmail}`);
      const onboardingComplete = localStorage.getItem('onboardingComplete');
      if (termsAccepted && !onboardingComplete) setShowOnboarding(true);
    }
  }, [isLoggedIn, userEmail, showTermsModal]);

  // ==========================================
  // GOOGLE LOGIN — stores ID token for API auth
  // ==========================================
  const responseGoogle = async (response) => {
    if (response.error) {
      setOutput('Google login failed. Please try again.');
      return;
    }

    const userCredential = response.credential;
    if (userCredential) {
      try {
        // ✅ Store the Google ID token — used as Bearer token for all API calls
        setAuthToken(userCredential);

        const userData = JSON.parse(atob(userCredential.split('.')[1]));
        const userEmail = userData.email;
        let isNewUser = false;

        let firstName = userData.given_name || userData.name?.split(' ')[0] || 'User';
        let lastName = userData.family_name || userData.name?.split(' ').slice(1).join(' ') || '';
        if (!firstName || firstName === 'User') firstName = userEmail.split('@')[0];

        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userEmail', userEmail);
        localStorage.setItem('userFirstName', firstName);
        localStorage.setItem('userLastName', lastName);

        try {
          // list_users is public — no auth needed
          const usersRes = await fetch(`${apiUrl}/list_users`);
          const existingUsers = await usersRes.json();

          if (!existingUsers.includes(userEmail)) {
            isNewUser = true;
            // register_user is public — no auth needed
            const registerRes = await fetch(`${apiUrl}/register_user`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userEmail,
                first_name: firstName,
                last_name: lastName,
                email: userEmail,
                phone: 'N/A',
                login_source: 'google'
              })
            });

            const responseText = await registerRes.text();
            const lowerResponse = responseText.toLowerCase();
            if (!registerRes.ok &&
              !lowerResponse.includes('already exists') &&
              !lowerResponse.includes('duplicate') &&
              registerRes.status !== 409) {
              alert('Registration failed. Please contact support.');
              return;
            }
          }
        } catch (err) {
          // Don't block login
        }

        // Apply referral code for new users
        const pendingReferralCode = sessionStorage.getItem('pending_referral_code');
        if (pendingReferralCode && isNewUser) {
          try {
            await authFetch(`${apiUrl}/apply_referral`, {
              method: 'POST',
              body: JSON.stringify({ user_id: userEmail, referral_code: pendingReferralCode })
            });
            sessionStorage.removeItem('pending_referral_code');
          } catch (error) {
            // Silent fail
          }
        }

        if (userData.picture) {
          localStorage.setItem('googlePhotoURL', userData.picture);
        } else {
          localStorage.removeItem('googlePhotoURL');
        }

        setIsLoggedIn(true);
        setUserEmail(userEmail);
        setUserFirstName(firstName);
        setUserLastName(lastName);

        fetchStoredUrls(userData.email);

        const redirectPath = localStorage.getItem('redirectAfterLogin');
        const savedUrl = localStorage.getItem("pendingUrl");
        const savedKeyword = localStorage.getItem("pendingKeyword");
        const savedKeywordsList = localStorage.getItem("pendingKeywordsList");

        if (savedUrl || savedKeyword || savedKeywordsList) {
          localStorage.setItem("triggerSearchAfterLogin", "true");
        }

        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
        } else {
          navigate("/tool");
        }

      } catch (error) {
        setOutput('Failed to parse user data. Please try again.');
      }
    } else {
      setOutput('Google login failed. Please try again.');
    }
  };

  useEffect(() => {
    window.testLogin = responseGoogle;
    return () => delete window.testLogin;
  }, []);

  const extractCompanyName = (url) => {
    try {
      const hostname = new URL(url).hostname;
      const parts = hostname.split('.');
      return parts[parts.length - 2];
    } catch (error) {
      return 'Unknown Company';
    }
  };

  const handleLogout = () => {
    googleLogout();
    // ✅ Clear auth token on logout
    clearAuthToken();
    setStoredResults([]);
    setOutput('');
    setUrl('');
    setKeyword('');
    setCurrentPage(1);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('loginSource');
    setIsLoggedIn(false);
    setUserEmail(null);
    navigate('/');
  };

  const handleStoreUrl = async (entry) => {
    if (!userEmail) {
      alert("No user logged in; cannot save.");
      return;
    }

    try {
      const baseSearchId = uuidv4();
      const theTime = Math.floor(Date.now() / 1000);
      const frequency = entry.frequency || "None";
      const base_url = entry.url?.includes("?") ? entry.url.split("?")[0] : entry.url;

      const keywordsData = entry.keywords.map((kw, index) => ({
        user_id: userEmail,
        search_id: `${baseSearchId}-${index + 1}`,
        url: kw.found === "exact" || kw.found === "partial" ? kw.url : entry.url,
        base_url,
        company_name: entry.company_name || extractCompanyName(entry.url || "") || "Unknown Company",
        keyword: kw.keyword || "Unknown",
        match_type: kw.found === "exact" ? "exact" : kw.found === "partial" ? "partial" : "not-found",
        runtime: entry.runtime || "N/A",
        time: theTime,
        frequency,
        mark_as_applied: false,
        correct_status: "unmark",
        match_status_changed: false,
        tracking_source: "website"
      }));

      for (const bodyToSend of keywordsData) {
        try {
          await authFetch(`${apiUrl}/store_url`, {
            method: "POST",
            body: JSON.stringify(bodyToSend),
          });
        } catch (err) {
          // Silent fail
        }
      }

      setStoredResults((prev) => {
        const newEntry = {
          search_id: baseSearchId,
          url: entry.url,
          base_url,
          company_name: entry.company_name || extractCompanyName(entry.url || "") || "Unknown Company",
          runtime: entry.runtime || "N/A",
          timeRaw: theTime,
          timeFormatted: new Date(theTime * 1000).toLocaleString(),
          keywords: entry.keywords.map((kw, index) => ({
            keyword: kw.keyword,
            match_type: kw.found === "exact" ? "exact" : kw.found === "partial" ? "partial" : "not-found",
            url: kw.url,
            search_id: `${baseSearchId}-${index + 1}`,
            mark_as_applied: false,
            correct_status: "unmark",
            match_status_changed: false,
          })),
          frequency,
        };
        return [newEntry, ...prev];
      });

      await fetchStoredUrls(userEmail);
    } catch (error) {
      // Silent fail
    }
  };

  const handleOnboardingComplete = async () => {
    const onboardingData = JSON.parse(localStorage.getItem('userOnboarding') || '{}');
    setShowOnboarding(false);
    if (onboardingData?.chromeExtension === true) {
      window.open('https://chrome.google.com/webstore/detail/YOUR_EXTENSION_ID', '_blank');
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Navbar />
      <div className="app-content">
        <Routes>
          <Route path="/linkedin-callback" element={<LinkedInCallback />} />
          <Route
            path="/signup"
            element={
              localStorage.getItem('isLoggedIn') === 'true' ? (
                <Navigate to="/tool" replace />
              ) : (
                <SignUp responseGoogle={responseGoogle} />
              )
            }
          />
          <Route path="/" element={<Home onOpenTool={() => navigate('/tool')} />} />
          <Route
            path="/tool"
            element={
              <div>
                <div className="toggle-container">
                  <div className="toggle-switch">
                    <div
                      className={`toggle-option ${selectedTab === 'Tool' ? 'active' : ''}`}
                      onClick={() => { setSelectedTab('Tool'); localStorage.setItem('selectedTab', 'Tool'); }}
                    >
                      Job Search
                    </div>
                    <div
                      className={`toggle-option ${selectedTab === 'Dashboard' ? 'active' : ''}`}
                      onClick={() => { setSelectedTab('Dashboard'); localStorage.setItem('selectedTab', 'Dashboard'); }}
                    >
                      Dashboard
                    </div>
                  </div>
                </div>
                <div className="toggle-content">
                  {selectedTab === 'Tool' ? (
                    <Tool
                      url={url} setUrl={setUrl}
                      keyword={keyword} setKeyword={setKeyword}
                      output={output}
                      storedResults={storedResults}
                      currentPage={currentPage}
                      setStoredResults={setStoredResults}
                      loading={loading} setLoading={setLoading}
                      handleStoreUrl={handleStoreUrl}
                      userPlan={userPlan}
                    />
                  ) : (
                    <Dashboard
                      storedResults={storedResults}
                      setStoredResults={setStoredResults}
                      fetchStoredUrls={fetchStoredUrls}
                      userPlan={userPlan}
                    />
                  )}
                </div>
              </div>
            }
          />
          <Route path="/profile" element={<ProtectedRoute><Profile handleLogout={handleLogout} userFirstName={userFirstName} userLastName={userLastName} userEmail={userEmail} storedResults={storedResults} /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
          <Route path="/outplacement" element={<OutplacementServices />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing-success" element={<PricingSuccess />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/extension" element={<Extension />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <TermsModal
        show={showTermsModal}
        onAgree={() => {
          localStorage.setItem(`termsAccepted_${userEmail}`, 'true');
          setShowTermsModal(false);
        }}
      />
      <OnboardingModal isOpen={showOnboarding} onComplete={handleOnboardingComplete} />
    </GoogleOAuthProvider>
  );
}

export default App;